/**
 * TypeDistribution - horizontal bar chart showing distribution of STRUCT types
 *
 * Each bar displays: type_code | gradient bar (scaled to maxCount) | count
 */

import React from 'react';
import styled from 'styled-components';

/* ------------------------------------------------------------------ */
/*  Styled components                                                  */
/* ------------------------------------------------------------------ */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TypeLabel = styled.span`
  flex-shrink: 0;
  width: 52px;
  font-family: ${(props) => props.theme.fonts.mono};
  font-size: 0.82rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  text-align: right;
`;

const BarTrack = styled.div`
  flex: 1;
  height: 22px;
  background: ${(props) => props.theme.colors.bgSecondary};
  border-radius: ${(props) => props.theme.borderRadius.sm};
  overflow: hidden;
  position: relative;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: ${(props) => props.theme.borderRadius.sm};
  background: ${(props) => props.$gradient};
  width: ${(props) => props.$pct}%;
  min-width: ${(props) => (props.$pct > 0 ? '4px' : '0')};
  transition: width 0.5s ease;
`;

const CountLabel = styled.span`
  flex-shrink: 0;
  width: 40px;
  font-family: ${(props) => props.theme.fonts.mono};
  font-size: 0.82rem;
  font-weight: 500;
  color: ${(props) => props.theme.colors.textSecondary};
  text-align: left;
`;

/* ------------------------------------------------------------------ */
/*  Gradient palette                                                   */
/* ------------------------------------------------------------------ */

const GRADIENTS = [
  'linear-gradient(90deg, #6366F1, #818CF8)',
  'linear-gradient(90deg, #8B5CF6, #A78BFA)',
  'linear-gradient(90deg, #EC4899, #F472B6)',
  'linear-gradient(90deg, #6366F1, #8B5CF6)',
  'linear-gradient(90deg, #8B5CF6, #EC4899)',
  'linear-gradient(90deg, #6366F1, #EC4899)',
  'linear-gradient(90deg, #A78BFA, #C084FC)',
  'linear-gradient(90deg, #818CF8, #A78BFA)',
  'linear-gradient(90deg, #F472B6, #FB7185)',
  'linear-gradient(90deg, #C084FC, #E879F9)',
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

function TypeDistribution({ data = [], maxCount }) {
  // Determine scale max: explicit prop or largest count in data
  const scaleMax =
    maxCount != null && maxCount > 0
      ? maxCount
      : Math.max(...data.map((d) => d.count), 1);

  // Sort descending by count for visual clarity
  const sorted = [...data].sort((a, b) => b.count - a.count);

  return (
    <Container>
      {sorted.map((item, idx) => {
        const pct = Math.min((item.count / scaleMax) * 100, 100);
        const gradient = GRADIENTS[idx % GRADIENTS.length];
        return (
          <Row key={item.type_code}>
            <TypeLabel>{item.type_code}</TypeLabel>
            <BarTrack>
              <BarFill $pct={pct} $gradient={gradient} />
            </BarTrack>
            <CountLabel>{item.count}</CountLabel>
          </Row>
        );
      })}
    </Container>
  );
}

export default TypeDistribution;
